<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { AustralianStates } from "../../../Utils/utilEnums.svelte";

    import {
        importGoogleMapScript,
        checkProviderNumberValidation,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import Select from "svelte-select/Select.svelte";

    import { bupaPractices } from "../../../stores";

    import { NotificationModal } from "../../../Components/Common/index.svelte";

    export let providerDetails = {},
        isModalOpen = false,
        reload = () => {},
        accountId,
        accountGroupId,
        isAdd = false;

    let isUpdatingProvider = false,
        primaryLocationAddress,
        errInputs = [],
        previousKeyDown;

    let linkedHospital;

    let filteredProvidersByAccountGroup = [];

    onMount(async () => {
       
    });

    $: if (isModalOpen) {
        importGoogleMapScript();
        syncProvidersByAccountGroup();
        _syncHospitalName();
    } else {
        primaryLocationAddress = null;
        linkedHospital = null;
    }

    $: isValidProviderNumber = checkProviderNumberValidation(
        providerDetails.number,
    );

    const clearSelectedPrimaryLocation = () => {
        primaryLocationAddress = null;

        _resetDefaultStyle("primaryLocationInput");
    };

    const syncProvidersByAccountGroup = () => {
        filteredProvidersByAccountGroup = $bupaPractices.filter(
            (bp) => bp.groupId == accountGroupId,
        );

        if (!isAdd && providerDetails.bupaPracticeId == 0) {
            const firstDefaultBupaPractice =
                filteredProvidersByAccountGroup.find((p) => p.isDefault);

            providerDetails.bupaPracticeId = firstDefaultBupaPractice.id ?? 0
        }
    };

    const handleSelectPrimaryLocation = async () => {
        await apiMethods.getMethod
            .getPlaceDetailsFromGoogle(primaryLocationAddress.place_id)
            .then((res) => {
                _syncLocationDetails(res);

                _resetDefaultStyle("primaryLocationAddressInput");
            });
    };

    // HELPER FUNCTIONS
    const _updateInvalidFieldsStyle = () => {
        if (!providerDetails.number || !isValidProviderNumber)
            _setErrorStyle("providerNumberInput");
        else _resetDefaultStyle("providerNumberInput");

        if (!providerDetails.locationAddress)
            _setErrorStyle("primaryLocationAddressInput");
        else _resetDefaultStyle("primaryLocationAddressInput");

        if (!providerDetails.state) _setErrorStyle("state");
        else _resetDefaultStyle("state");

        if (errInputs.length) {
            const scrollToElement = document.getElementById(errInputs[0]);
            if (scrollToElement)
                scrollToElement.scrollIntoView({ behavior: "smooth" });
        }
    };

    const checkProviderValidation = () => {
        errInputs = [];
        providerDetails = { ...providerDetails };
        _updateInvalidFieldsStyle();

        return errInputs.length;
    };

    const _setErrorStyle = (id) => {
        errInputs.push(id);
        const field = document.getElementById(`${id}`);
        if (id) field.style.outline = "red solid 2px";
    };

    const _resetDefaultStyle = (id) => {
        const field = document.getElementById(`${id}`);
        if (field) field.style.outline = "none";
    };

    const loadLocationAddress = async (searchText) => {
        if (!searchText) return [];

        let res = await apiMethods.getMethod.getAddressFromGoogle(searchText);

        if (res && res.predictions) return [...res.predictions];
    };

    const _syncLocationDetails = (details) => {
        // providerDetails.location = primaryLocationAddress.description;

        providerDetails.locationAddress = details.formatted_address;

        if (
            primaryLocationAddress &&
            primaryLocationAddress.structured_formatting
        ) {
            const { main_text } = primaryLocationAddress.structured_formatting;
            providerDetails.location = main_text;
        }

        if (details.address_components)
            for (let component of details.address_components) {
                let types = component["types"];

                if (types.includes("locality"))
                    providerDetails.city = component["long_name"];
                else if (types.includes("country"))
                    providerDetails.country = component["long_name"];
                else if (types.includes("postal_code"))
                    providerDetails.postCode = component["long_name"];
                else if (types.includes("street_number"))
                    providerDetails.streetNumber = component["long_name"];
                else if (types.includes("route"))
                    providerDetails.streetName = component["long_name"];
                else if (types.includes("administrative_area_level_1"))
                    // use short name to match backend requests and sync with local constants
                    providerDetails.state = component["short_name"];
            }
    };

    const proceed = async () => {
        if (isAdd) createAccountProvider();
        else updateAccountProvider();
    };

    const createAccountProvider = async () => {
        if (checkProviderValidation()) return;
        if (!accountId) return showError("Doctor ID missing");
        providerDetails.doctorId = accountId;

        isUpdatingProvider = true;

        //set hospital
        if(linkedHospital != null){
            providerDetails.hospitals = [{ hospitalId: linkedHospital.id }]
        }

        await apiMethods.postMethod
            .createAccountProvider(providerDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error
                            ? "Provider number must be unique for this doctor account. This provider number is already registered."
                            : "Failed to create account provider, please refresh the page and try again and contact support if the issue persists.",
                    );
                else reload();
            });

        isUpdatingProvider = false;
    };

    const updateAccountProvider = async () => {
        isUpdatingProvider = true;
        //set hospital
        if(linkedHospital != null){
            providerDetails.hospitals = [{ hospitalId: linkedHospital.id }]
        } else {
            if(linkedHospital == null && providerDetails.hospitals != null && providerDetails.hospitals.length > 0) {
                //hospital was removed
                providerDetails.hospitals = [];
            }
        }
        await apiMethods.putMethod
            .updateAccountProvider(providerDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error
                            ? "Provider number must be unique for this doctor account. This provider number is already registered."
                            : "Failed to update account provider, please refresh the page and try again and contact support if the issue persists.",
                    );
                else reload();
            });

        isUpdatingProvider = false;
    };

    $: confirmButtonText = `${isAdd ? "Creat" : "Updat"}${
        isUpdatingProvider ? "ing" : "e"
    }`;

    const checkAndAutoPadProviderNumber = () => {
        if (
            providerDetails.number &&
            providerDetails.number.length < 8 &&
            !isValidProviderNumber
        ) {
            providerDetails.number = providerDetails.number.padStart(8, "0");
        }
    };

    const closeModal = () => {
        isModalOpen = false;
        providerDetails = {};
        document.onkeydown = previousKeyDown;
    };

    const loadHospitals = async (searchText = "") => {
        if (!searchText) return [];

        let res = await apiMethods.getMethod.getHospitals(0, 999999, {
            search: searchText,
        });

        if (res && res.results) return res.results;

        return [];
    };

    const _syncHospitalName = async () => {
        if(!isAdd && providerDetails && providerDetails.hospitals && providerDetails.hospitals.length > 0){
            var selectedHospital = providerDetails.hospitals[0];
            await apiMethods.getMethod
                .getHospitals(0, 999999, { "filter[id]": selectedHospital.hospitalId })
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to load hospital details, please try again later or contact support...",
                        );
                    else
                        linkedHospital = res.results[0] ?? null
                });
        }
        

    };

    const handleSelectLinkedHospital = (data) => {
        if (!data) return;

        const { detail } = data;

        if (!detail) return;

        const { id } = detail;
        
    };
</script>

<NotificationModal
    {isModalOpen}
    title={`${isAdd ? "Add" : "Edit"} Provider`}
    modalMaxWidth="max-w-[648px]"
    cancelText="Cancel"
    submitText={confirmButtonText}
    isConfirming={isUpdatingProvider}
    {closeModal}
    closeModalFunction={closeModal}
    confirmAction={proceed}
    noContentPadding
>
    <div class="flex flex-1 flex-col gap-y-4 relative overflow-y-auto p-6">
        <div class="flex gap-x-4 items-center">
            <label class="w-48 mandatory" for="providerNumberInput"
                >Provider Number:</label
            >
            <input
                type="text"
                id="providerNumberInput"
                class="input input-sm input-bordered flex-1"
                disabled={!isAdd}
                bind:value={providerDetails.number}
                on:blur={checkAndAutoPadProviderNumber}
            />
        </div>
        {#if !isValidProviderNumber}
            <p class="text-error w-full">
                Please provide a valid provider number
            </p>
        {/if}

        <div class="flex flex-1" id="primaryLocationInput">
            <Select
                class="rounded-full"
                bind:value={primaryLocationAddress}
                loadOptions={loadLocationAddress}
                itemId="place_id"
                label="description"
                on:clear={clearSelectedPrimaryLocation}
                on:select={handleSelectPrimaryLocation}
                noOptionsMessage="No available locations found"
                placeholder="Type and select location"
            />
        </div>

        <div class="flex gap-x-4 items-center">
            <label for="postcode" class="w-48">Location name:</label>
            <input
                type="text"
                id="postcode"
                class="input input-sm input-bordered flex-1"
                bind:value={providerDetails.location}
            />
        </div>

        <div class="flex gap-x-4 items-center">
            <label for="primaryLocationAddressInput" class="w-48 mandatory">
                Location Address:
            </label>
            <input
                type="text"
                id="primaryLocationAddressInput"
                class="input input-sm input-bordered flex-1"
                bind:value={providerDetails.locationAddress}
                on:focus={() =>
                    _resetDefaultStyle("primaryLocationAddressInput")}
            />
        </div>

        <!-- <div class="flex gap-x-4 items-center">
            <label for="locationAddress2" class="w-48"
                >Secondary Location Address:</label
            >
            <input
                type="text"
                id="locationAddress2"
                class="input input-sm input-bordered flex-1"
                bind:value={providerDetails.locationAddress2}
            />
        </div> -->

        <div class="flex gap-x-4 items-center">
            <label for="city" class="w-48">City:</label>
            <input
                type="text"
                id="city"
                class="input input-sm input-bordered flex-1"
                bind:value={providerDetails.city}
            />
        </div>

        <div class="flex gap-x-4 items-center">
            <label for="state" class="w-48 mandatory">State:</label>
            <select
                class="input input-sm input-bordered flex-1"
                id="state"
                bind:value={providerDetails.state}
            >
                {#each AustralianStates as state}
                    <option value={state.key}>
                        {state.name}
                    </option>
                {/each}
            </select>
        </div>

        <div class="flex gap-x-4 items-center">
            <label for="postcode" class="w-48">Postcode:</label>
            <input
                type="text"
                id="postcode"
                class="input input-sm input-bordered flex-1"
                bind:value={providerDetails.postCode}
            />
        </div>

        <div class="flex gap-x-4 items-center">
            <label for="linkedHospital" class="w-48 flex-none">
                Link to hospital:
            </label>
            <div class="flex-1 min-w-0">
                <Select
                    bind:value={linkedHospital}
                    class="w-full max-h-[35px] rounded-full"
                    loadOptions={loadHospitals}
                    itemId="id"
                    label="name"
                    on:select={handleSelectLinkedHospital}
                    on:clear={() => (linkedHospital = null)}
                    placeholder="Search for a hospital to link"
                />
            </div>
        </div>

        <div class="flex gap-x-4 items-center">
            <p class="w-48">isDefault:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={providerDetails.isDefault}
            >
                <option value={Boolean(true)}>Yes</option>
                <option value={Boolean(false)}>No</option>
            </select>
        </div>

        <div class="flex gap-x-4 items-center">
            <p class="w-48">BUPA:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={providerDetails.isNoGapBupa}
            >
                <option value={Boolean(true)}>No Gap</option>
                <option value={Boolean(false)}>Known Gap</option>
            </select>
        </div>

        <div class="flex gap-x-4 items-center">
            <p class="w-48">BUPA Practice ID:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={providerDetails.bupaPracticeId}
            >
                {#each filteredProvidersByAccountGroup.sort((a, b) => a.id - b.id) as { id, practiceId, state }}
                    <option value={id}>
                        {practiceId}
                        {state ? `(${state})` : ""}
                    </option>
                {/each}
            </select>
        </div>

        <div class="flex gap-x-4 items-center">
            <p class="w-48">HCF:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={providerDetails.isNoGapHCF}
            >
                <option value={Boolean(true)}>No Gap</option>
                <option value={Boolean(false)}>Known Gap</option>
            </select>
        </div>
        {#if providerDetails.state && providerDetails.state == "WA"}
            <div class="flex gap-x-4 items-center">
                <p class="w-48">HBF WA Cover Type:</p>
                <select
                    class="select select-sm select-bordered flex-1"
                    bind:value={providerDetails.isFullyCoveredHBFWA}
                >
                    <option value={0}>Inactive</option>
                    <option value={1}>Provider Choice</option>
                    <option value={2}>Fully Covered</option>
                </select>
            </div>
        {/if}

        <div class="flex gap-x-4 items-center">
            <p class="w-48">NIB:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={providerDetails.isNoGapNib}
            >
                <option value={Boolean(true)}>Medigap</option>
                <option value={Boolean(false)}>GapSure</option>
            </select>
        </div>

        <div class="flex gap-x-4 items-center">
            <p class="w-48">Porting Existing Number:</p>
            <select
                class="select select-sm select-bordered flex-1"
                bind:value={providerDetails.portingExistingNumber}
            >
                <option value={Boolean(true)}>Yes</option>
                <option value={Boolean(false)}>No</option>
            </select>
        </div>

        <div class="flex gap-x-4 items-center">
            <input
                type="checkbox"
                id="isPreferredDefence"
                class="toggle toggle-secondary"
                bind:checked={providerDetails.isPreferredDefence}
            />
            <label for="isPreferredDefence">
                Preferred provider for Australian Defence Force? (leave
                unchecked if unsure)</label
            >
        </div>

        <!-- this field only exists at frontend, hide in edit for now -->
        <!-- {#if isAdd}
            <div class="flex gap-x-4 items-center">
                <input
                    type="checkbox"
                    id="isPortingAcross"
                    class="checkbox checkbox-sm checkobx-bordered checkbox-secondary"
                    bind:checked={providerDetails.isPortingAcross}
                />
                <label for="isPortingAcross">
                    This provider number has previously been registered with
                    health fund</label
                >
            </div>
        {/if} -->
    </div>
</NotificationModal>

<style>
    .mandatory:after {
        content: " * ";
    }
</style>
